import styled from 'styled-components';

import { CheckboxGroup } from '@components/checkbox-group';
import { EmbedVideo } from '@components/embed-video';
import { HtmlContent } from '@components/html-content';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { pxToRem } from '@utils/converters';
import { uuid } from '@utils/uuid';

import { ConditionalDisplay } from './conditions';
import { FieldType, FormAnswerFieldType, FormAnswers, FormV2 } from './types';

import { Image } from '../../form-builder/fields/image';
import { createForm } from '../create-form';
import { FormValidatorType } from '../types';

const Form = createForm();

export const DynamicFields = ({
  fields,
  formAnswers,
  preview = false,
}: {
  fields: FormV2['fields'];
  formAnswers?: FormAnswers;
  preview?: boolean;
}) => {
  return (
    <Stack space={4}>
      {fields.map((field) => (
        <RenderField key={field.id} field={field} preview={preview} formAnswers={formAnswers} />
      ))}
    </Stack>
  );
};

const getFormFieldForField = (field: FieldType, preview = false, formAnswers?: FormAnswers) => {
  const fieldAnswer = formAnswers?.find((answer: FormAnswerFieldType) => answer.id === field.id);

  const validators: FormValidatorType[] =
    'isRequired' in field && field.isRequired ? ['required'] : [];

  switch (field.kind) {
    case 'submit': {
      return (
        <div>
          <Form.Actions>
            {field.saveLabel && (
              <Form.Submit
                isDisabled={preview}
                text={field.saveLabel}
                variant="primary-blue"
                action="save"
                shouldDisableWhenNotDirty={true}
              />
            )}
            <Form.Submit isDisabled={preview} text={field.label} />
          </Form.Actions>
          <Form.Errors />
        </div>
      );
    }

    case 'input-date': {
      return (
        <Form.DatePicker
          label={field.label}
          id={field.id}
          validators={validators}
          placeholder={field.placeholder}
          defaultValue={field.value}
        />
      );
    }
    case 'input-file': {
      const fileFieldAnswer = fieldAnswer as Extract<FormAnswerFieldType, { kind: 'input-file' }>;
      const fileValues = Array.isArray(fileFieldAnswer?.value) ? fileFieldAnswer?.value : [];
      const originalAttachmentIds =
        typeof field.value === 'number' ? [field.value] : field.value || [];

      const files = fileValues.map((file, index) => {
        return {
          id: uuid(),
          name: file?.name ?? '',
          src: file?.src ?? '',
          attachmentId: originalAttachmentIds[index] as number,
        };
      });

      if (field.allowMultiple || (field.maximumFileCount && field.maximumFileCount > 1)) {
        return (
          <Form.FileFieldMulti
            label={field.label}
            accept={field.accept}
            id={field.id}
            validators={validators}
            placeholder={field.placeholder}
            defaultValue={files.length > 0 ? files : undefined}
          />
        );
      }
      const defaultFile = files.length > 0 ? files[0] : undefined;

      return (
        <Form.FileField
          label={field.label}
          accept={field.accept}
          id={field.id}
          placeholder={field.placeholder}
          validators={validators}
          defaultValue={defaultFile}
        />
      );
    }

    case 'input-phone': {
      return (
        <Form.PhoneField
          id={field.id}
          label={field.label}
          validators={validators}
          isCountryPrefilled
          defaultValue={field.value}
        />
      );
    }

    case 'input-email': {
      return field.hasConfirmation && field.isRequired ? (
        <Stack space={4}>
          <Form.EmailTextField
            id={field.id}
            validators={validators}
            label={field.label}
            placeholder={field.placeholder}
          />
          <Form.EmailTextField
            id={`${field.id}-confirmation`}
            validators={validators}
            label={field.confirmLabel}
            placeholder={field.placeholder}
          />
        </Stack>
      ) : (
        <Form.EmailTextField
          id={field.id}
          validators={validators}
          label={field.label}
          placeholder={field.placeholder}
          defaultValue={field.value}
        />
      );
    }

    case 'input-text-large': {
      return (
        <Form.Textarea
          id={field.id}
          label={field.label}
          validators={validators}
          placeholder={field.placeholder}
          defaultValue={field.value}
        />
      );
    }

    case 'input-text-small': {
      return (
        <Form.TextField
          validators={validators}
          id={field.id}
          label={field.label}
          placeholder={field.placeholder}
          maxLength={255}
          defaultValue={field.value}
        />
      );
    }
    case 'input-kennitala': {
      const kennitalaValidators: FormValidatorType[] = ['ssn', ...(validators ?? [])];
      return (
        <Form.TextField
          validators={kennitalaValidators}
          id={field.id}
          label={field.label}
          placeholder={field.placeholder}
          maxLength={10}
          defaultValue={field.value}
        />
      );
    }

    case 'input-number': {
      return (
        <Form.NumberField
          validators={validators}
          id={field.id}
          label={field.label}
          placeholder={field.placeholder ?? ''}
          min={field.restrictMin && field.min !== null ? field.min : undefined}
          max={field.restrictMax && field.max !== null ? field.max : undefined}
          disableNegative={!field.allowNegative}
          isDecimal={field.isDecimal}
          defaultValue={field.value}
        />
      );
    }

    case 'video': {
      if (!field.url) return null;
      return <EmbedVideo url={field.url} />;
    }

    case 'section': {
      if (field.fields.length === 0) {
        return null;
      }

      return (
        <SectionContainer data-columns={field.columns} data-kind="section">
          {field.fields.map((child) => (
            <RenderField key={child.id} field={child} formAnswers={formAnswers} />
          ))}
        </SectionContainer>
      );
    }

    case 'input-dropdown': {
      return (
        <Form.Select
          id={field.id}
          label={field.label}
          options={field.options}
          defaultValue={field.value ?? field.defaultValue ?? undefined}
          validators={validators}
          placeholder={field.placeholder}
        />
      );
    }

    case 'input-single-choice': {
      return (
        <Form.RadioGroup
          id={field.id}
          label={field.label}
          options={field.options}
          defaultValue={field.value ?? field.defaultValue ?? undefined}
          validators={validators}
        />
      );
    }

    case 'input-multiple-choice': {
      return (
        <Form.CheckboxGroup
          id={field.id}
          label={field.label}
          defaultValue={field.value ?? field.defaultValue ?? undefined}
          validators={validators}
        >
          {field.options.map((option) => {
            return (
              <CheckboxGroup.Item key={option.value} label={option.label} value={option.value} />
            );
          })}
        </Form.CheckboxGroup>
      );
    }

    case 'heading': {
      return <Text font="heading1" text={field.label} />;
    }

    case 'subheading': {
      return <Text font="heading2" text={field.label} />;
    }

    case 'separator': {
      return <Separator />;
    }

    case 'html': {
      return <HtmlContent html={field.content} />;
    }

    case 'image':
      return field.url ? <Image src={field.url} $width={field.imageWidth} /> : null;

    default:
      return null;
  }
};

const RenderField = ({
  field,
  preview,
  formAnswers,
}: {
  field: FieldType;
  preview?: boolean;
  formAnswers?: FormAnswers;
}) => {
  const formField = getFormFieldForField(field, preview, formAnswers);

  if (!formField) return null;

  if (field.kind === 'submit') {
    return <>{formField}</>;
  }

  return <ConditionalDisplay field={field}>{formField}</ConditionalDisplay>;
};

const SectionContainer = styled.div({
  display: 'grid',
  gap: pxToRem(8),
  '[data-columns="2"] &': {
    gridColumn: '1 / span 2',
  },

  '[data-mobile="false"] &': {
    '&[data-columns="2"]': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },

  '[data-mobile="true"] &': {
    display: 'flex',
    flexDirection: 'column',
  },
});
